import type { CellState, Coord, MoveTransaction, PuzzleDefinition } from '../types';

export const NEXT_CELL_STATE: Record<CellState, CellState> = {
  empty: 'excluded', excluded: 'shiba', shiba: 'empty',
};

export const blankCells = (size: number, givens: Coord[] = []): CellState[][] => {
  const cells = Array.from({ length: size }, () => Array<CellState>(size).fill('empty'));
  givens.forEach(({ row, col }) => { cells[row][col] = 'shiba'; });
  return cells;
};

export const isGiven = (puzzle: PuzzleDefinition, row: number, col: number) =>
  puzzle.givenShibas.some((given) => given.row === row && given.col === col);

export const cycleCell = (cells: CellState[][], puzzle: PuzzleDefinition, row: number, col: number): { cells: CellState[][]; transaction: MoveTransaction | null } => {
  if (isGiven(puzzle, row, col)) return { cells, transaction: null };
  const before = cells[row][col];
  const after = NEXT_CELL_STATE[before];
  const next = cells.map((line) => [...line]);
  next[row][col] = after;
  return { cells: next, transaction: { source: 'player', changes: [{ row, col, before, after }] } };
};

export const applyTransaction = (cells: CellState[][], transaction: MoveTransaction): CellState[][] => {
  const next = cells.map((line) => [...line]);
  transaction.changes.forEach(({ row, col, after }) => { next[row][col] = after; });
  return next;
};

export const undoTransaction = (cells: CellState[][], transaction: MoveTransaction): CellState[][] => {
  const next = cells.map((line) => [...line]);
  transaction.changes.forEach(({ row, col, before }) => { next[row][col] = before; });
  return next;
};

export type ConflictKind = 'row' | 'column' | 'region' | 'touching';
export interface Conflict { kind: ConflictKind; cells: Coord[] }

export const findConflicts = (cells: CellState[][], puzzle: PuzzleDefinition): Conflict[] => {
  const shibas: Coord[] = [];
  cells.forEach((line, row) => line.forEach((state, col) => { if (state === 'shiba') shibas.push({ row, col }); }));
  const result: Conflict[] = [];
  const addGroups = (kind: ConflictKind, key: (coord: Coord) => number) => {
    const groups = new Map<number, Coord[]>();
    shibas.forEach((coord) => groups.set(key(coord), [...(groups.get(key(coord)) ?? []), coord]));
    groups.forEach((group) => { if (group.length > 1) result.push({ kind, cells: group }); });
  };
  addGroups('row', ({ row }) => row);
  addGroups('column', ({ col }) => col);
  addGroups('region', ({ row, col }) => puzzle.regions[row][col]);
  for (let i = 0; i < shibas.length; i += 1) {
    for (let j = i + 1; j < shibas.length; j += 1) {
      const a = shibas[i]; const b = shibas[j];
      if (Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col)) <= 1) result.push({ kind: 'touching', cells: [a, b] });
    }
  }
  return result;
};

export const isCompleted = (cells: CellState[][], puzzle: PuzzleDefinition): boolean => {
  const count = cells.flat().filter((cell) => cell === 'shiba').length;
  return count === puzzle.size && findConflicts(cells, puzzle).length === 0;
};

